import React from 'react';
import ReactPaginate from 'react-paginate';
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

const PAGE_ARROW_ICON_SX = { height: '22px', width: '22px' };

type TablePaginatorProps = {
  pageCount: number;
  /** 1-based current page */
  page: number;
  onPageChange: (page: number) => void;
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
  /** /logs: each page is fetched from searchlogs — total count is unknown. */
  fetchLogsFromDb?: boolean;
  /** When fetchLogsFromDb, next arrow stays on until the BE returns a short page. */
  hasMorePages?: boolean;
  /** Highest page already loaded — keep those numbers when walking back. */
  loadedPageCount?: number;
  disabled?: boolean;
};

const pageArrowIcon = (Icon: typeof ArrowLeft) => (
  <Icon fontSize="small" sx={PAGE_ARROW_ICON_SX} />
);

const TablePaginator = ({
  pageCount,
  page,
  onPageChange,
  pageRangeDisplayed = 2,
  marginPagesDisplayed = 2,
  fetchLogsFromDb = false,
  hasMorePages = false,
  loadedPageCount = 1,
  disabled = false,
}: TablePaginatorProps) => {
  // DB-backed: keep visited page numbers when returning to an earlier page.
  if (fetchLogsFromDb) {
    const lastShownPage = Math.max(1, page, loadedPageCount);
    const atFirstPage = page <= 1;
    const atLastPage = !hasMorePages && page >= lastShownPage;
    const pageNumbers = Array.from({ length: lastShownPage }, (_, i) => i + 1);
    return (
      <div className={`pagination${disabled ? ' pagination--disabled' : ''}`}>
        <span className={`page-arrow${atFirstPage || disabled ? ' disabled' : ''}`}>
          <button
            type="button"
            className="page-arrow-link"
            disabled={atFirstPage || disabled}
            aria-label="Previous page"
            onClick={() => {
              if (!atFirstPage && !disabled) onPageChange(page - 1);
            }}
          >
            {pageArrowIcon(ArrowLeft)}
          </button>
        </span>
        {pageNumbers.map((n) => (
          <span key={n} className={`page-item${n === page ? ' active' : ''}`}>
            <button
              type="button"
              className="page-item-link"
              disabled={disabled}
              aria-label={`Page ${n}`}
              aria-current={n === page ? 'page' : undefined}
              onClick={() => {
                if (!disabled && n !== page) onPageChange(n);
              }}
            >
              {n}
            </button>
          </span>
        ))}
        <span className={`page-arrow${atLastPage || disabled ? ' disabled' : ''}`}>
          <button
            type="button"
            className="page-arrow-link"
            disabled={atLastPage || disabled}
            aria-label="Next page"
            onClick={() => {
              if (!atLastPage && !disabled) onPageChange(page + 1);
            }}
          >
            {pageArrowIcon(ArrowRight)}
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className={`pagination${disabled ? ' pagination--disabled' : ''}`}>
      <ReactPaginate
        previousLabel={pageArrowIcon(ArrowLeft)}
        nextLabel={pageArrowIcon(ArrowRight)}
        pageClassName="page-item"
        pageLinkClassName="page-item-link"
        previousClassName="page-arrow"
        previousLinkClassName="page-arrow-link"
        nextClassName="page-arrow"
        nextLinkClassName="page-arrow-link"
        breakLabel={pageArrowIcon(MoreHoriz)}
        breakClassName="page-arrow"
        breakLinkClassName="page-arrow-link"
        pageCount={Math.max(0, pageCount)}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={marginPagesDisplayed}
        onPageChange={(e) => {
          if (disabled) return;
          onPageChange(e.selected + 1);
        }}
        containerClassName="pagination-pages"
        activeClassName="active"
        forcePage={Math.max(0, page - 1)}
      />
    </div>
  );
};

export default TablePaginator;
